import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Dialog, SpinnerWithBackground, Text } from '@viapeople/components'
import { useState } from 'react'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import { CreateConnectionModal } from './CreateConnectionModal'

interface ConnectionsTabProps {
	organizationId: string
}

export function ConnectionsTab({ organizationId }: ConnectionsTabProps) {
	const { showToast } = useToast()
	const queryClient = useQueryClient()
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
	const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
	const [authorizingConnectionId, setAuthorizingConnectionId] = useState<string | null>(null)
	const [authenticatingDealCloudId, setAuthenticatingDealCloudId] = useState<string | null>(null)
	const [testingConnectionId, setTestingConnectionId] = useState<string | null>(null)

	// Fetch authorization URL when a pending connection needs OAuth
	const { data: authUrlData, isFetching: isFetchingAuthUrl } = useQuery({
		...trpc.admin.integrations.crmConnection.getAuthorizationUrl.queryOptions({
			organizationId,
			connectionId: authorizingConnectionId as string,
			adminUrl: `${window.location.origin}${window.location.pathname}`,
		}),
		enabled: !!authorizingConnectionId,
	})

	const handleAuthorize = (connectionId: string) => {
		setAuthorizingConnectionId(connectionId)
	}

	// Redirect once we have the auth URL
	if (authUrlData?.url && authorizingConnectionId) {
		window.location.href = authUrlData.url
	}

	const { data: connections, isLoading } = useQuery({
		...trpc.admin.integrations.crmConnection.getByOrganization.queryOptions({
			organizationId,
		}),
		enabled: !!organizationId,
	})

	const testMutation = useMutation({
		...trpc.admin.integrations.crmConnection.testConnection.mutationOptions(),
		onSuccess: (data) => {
			if (data.ok) {
				showToast('Connection is healthy')
			} else {
				showToast(`Connection test failed: ${data.error}`, 'error')
			}
			setTestingConnectionId(null)
			queryClient.invalidateQueries(
				trpc.admin.integrations.crmConnection.getByOrganization.queryFilter({ organizationId }),
			)
		},
		onError: (err) => {
			showToast(`Test failed: ${err.message}`, 'error')
			setTestingConnectionId(null)
		},
	})

	const handleTestConnection = (connectionId: string) => {
		setTestingConnectionId(connectionId)
		testMutation.mutate({ id: connectionId })
	}

	const authenticateDealCloudMutation = useMutation({
		...trpc.admin.integrations.crmConnection.authenticateDealCloud.mutationOptions(),
		onSuccess: () => {
			showToast('DealCloud connection authenticated')
			setAuthenticatingDealCloudId(null)
			queryClient.invalidateQueries(
				trpc.admin.integrations.crmConnection.getByOrganization.queryFilter({ organizationId }),
			)
		},
		onError: (err) => {
			showToast(`DealCloud authentication failed: ${err.message}`, 'error')
			setAuthenticatingDealCloudId(null)
		},
	})

	const handleReauthDealCloud = (connectionId: string) => {
		setAuthenticatingDealCloudId(connectionId)
		authenticateDealCloudMutation.mutate({ connectionId })
	}

	const deleteMutation = useMutation({
		...trpc.admin.integrations.crmConnection.delete.mutationOptions(),
		onSuccess: () => {
			showToast('Connection deleted')
			setDeleteTarget(null)
			queryClient.invalidateQueries(
				trpc.admin.integrations.crmConnection.getByOrganization.queryFilter({ organizationId }),
			)
		},
		onError: (err) => showToast(`Failed to delete: ${err.message}`, 'error'),
	})

	const getStatusBadge = (status: string) => {
		const config: Record<string, { bg: string; text: string; label: string }> = {
			connected: { bg: 'bg-green-100', text: 'text-green-700', label: 'Connected' },
			disconnected: { bg: 'bg-neutral-100', text: 'text-neutral-600', label: 'Disconnected' },
			pending_setup: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Pending Setup' },
			error: { bg: 'bg-red-100', text: 'text-red-700', label: 'Error' },
		}
		const c = config[status] || config.disconnected
		return <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${c.bg} ${c.text}`}>{c.label}</span>
	}

	const getTimeAgo = (date: Date | string | null | undefined) => {
		if (!date) return 'Never'
		const d = new Date(date)
		return d.toLocaleDateString()
	}

	if (isLoading) {
		return (
			<div className='py-12'>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</div>
		)
	}

	const connectionList = connections || []

	return (
		<div className='flex flex-col gap-4'>
			<div className='flex justify-between items-center'>
				<Text variant='medium' weight='semibold'>
					CRM Connections
				</Text>
				<Button variant='primary' size='medium' onClick={() => setIsCreateModalOpen(true)}>
					+ Add Connection
				</Button>
			</div>

			{connectionList.length === 0 ? (
				<div className='py-12 flex flex-col items-center justify-center border-2 border-dashed border-neutral-300 rounded-lg'>
					<Text variant='medium' weight='semibold' className='text-neutral-600 mb-2'>
						No Connections
					</Text>
					<Text variant='small' className='text-neutral-500 mb-4 text-center max-w-md'>
						Connect your CRM to enable integrations.
					</Text>
				</div>
			) : (
				<div className='border border-neutral-200 rounded-lg overflow-hidden bg-white shadow-sm'>
					<div className='grid grid-cols-[1fr_120px_140px_120px_160px] gap-4 px-6 py-3 bg-neutral-50 border-b border-neutral-200'>
						<Text variant='xsmall' weight='semibold' className='text-neutral-500 uppercase tracking-wider'>
							Name
						</Text>
						<Text variant='xsmall' weight='semibold' className='text-neutral-500 uppercase tracking-wider'>
							Provider
						</Text>
						<Text variant='xsmall' weight='semibold' className='text-neutral-500 uppercase tracking-wider'>
							Connected At
						</Text>
						<Text variant='xsmall' weight='semibold' className='text-neutral-500 uppercase tracking-wider'>
							Status
						</Text>
						<Text variant='xsmall' weight='semibold' className='text-neutral-500 uppercase tracking-wider text-right'>
							Actions
						</Text>
					</div>

					{connectionList.map((conn) => (
						<div
							key={conn.id}
							className='grid grid-cols-[1fr_120px_140px_120px_160px] gap-4 px-6 py-4 items-center border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 transition-colors'>
							<Text variant='small' weight='semibold' className='truncate text-neutral-900'>
								{conn.name}
							</Text>
							<Text variant='small' className='text-neutral-600 capitalize'>
								{conn.provider}
							</Text>
							<Text variant='small' className='text-neutral-600'>
								{getTimeAgo(conn.connectedAt)}
							</Text>
							<div>{getStatusBadge(conn.status)}</div>
							<div className='flex justify-end gap-1'>
								{conn.status === 'pending_setup' && conn.provider === 'salesforce' && (
									<Button
										variant='primary'
										size='small'
										onClick={() => handleAuthorize(conn.id)}
										disabled={isFetchingAuthUrl && authorizingConnectionId === conn.id}>
										{isFetchingAuthUrl && authorizingConnectionId === conn.id ? 'Redirecting...' : 'Authorize'}
									</Button>
								)}
								{conn.status === 'pending_setup' && conn.provider === 'dealcloud' && (
									<Button
										variant='primary'
										size='small'
										onClick={() => handleReauthDealCloud(conn.id)}
										disabled={authenticateDealCloudMutation.isPending && authenticatingDealCloudId === conn.id}>
										{authenticateDealCloudMutation.isPending && authenticatingDealCloudId === conn.id
											? 'Authenticating...'
											: 'Authenticate'}
									</Button>
								)}
								{conn.status === 'connected' && (
									<Button
										variant='secondary'
										size='small'
										onClick={() => handleTestConnection(conn.id)}
										disabled={testingConnectionId === conn.id}>
										{testingConnectionId === conn.id ? 'Testing...' : 'Test'}
									</Button>
								)}
								<button
									type='button'
									onClick={() => setDeleteTarget(conn)}
									className='p-1.5 rounded-md hover:bg-neutral-200 text-neutral-400 hover:text-red-600 transition-colors cursor-pointer'
									title='Delete Connection'>
									<svg
										className='w-4 h-4'
										fill='none'
										viewBox='0 0 24 24'
										stroke='currentColor'
										strokeWidth={1.5}
										aria-hidden='true'>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											d='M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0'
										/>
									</svg>
								</button>
							</div>
						</div>
					))}
				</div>
			)}

			{isCreateModalOpen && (
				<CreateConnectionModal
					isOpen={isCreateModalOpen}
					onClose={() => setIsCreateModalOpen(false)}
					organizationId={organizationId}
					onComplete={() => {
						setIsCreateModalOpen(false)
						queryClient.invalidateQueries(
							trpc.admin.integrations.crmConnection.getByOrganization.queryFilter({
								organizationId,
							}),
						)
					}}
				/>
			)}

			<Dialog
				isOpen={!!deleteTarget}
				onClose={() => setDeleteTarget(null)}
				onConfirm={() => deleteTarget && deleteMutation.mutate({ id: deleteTarget.id })}
				title='Delete Connection'
				message={`Are you sure you want to delete "${deleteTarget?.name}"? Any active integrations using this connection might fail.`}
				type='delete'
				confirmLabel='Delete'
				requireConfirmText
				confirmText='DELETE'
				isConfirmLoading={deleteMutation.isPending}
			/>
		</div>
	)
}
